import React from "react";
import {
  Box,
  Flex,
  Text,
  Divider,
  Stack,
} from "@chakra-ui/react";
import {
  MapPin,
  Navigation,
  Calendar,
  Truck,
  Package,
  CircleDot,
  Clock,
} from "lucide-react";

import ReusableDrawer from "components/Drawers/ReusableDrawer";
import GradientButton from "components/Buttons/GradientButton";
import StatusBadge, {
  getStatusVariant,
} from "components/Badges/StatusBadge";
import InfoRow from "components/InfoRow/InfoRow";
import headerImage from "../../../../assets/img/longarrow.jpg";
import { formatDate } from "utils/dateFormatter";

const ViewDriverTrip = ({ isOpen, onClose, trip }) => {
  if (!trip) return null;

  const route = `${trip.origin || "N/A"} → ${
    trip.destination || "N/A"
  }`;

  return (
    <ReusableDrawer
      isOpen={isOpen}
      onClose={onClose}
      headerImage={headerImage}
      headerContent={<h2>View Trip</h2>}
      bodyContent={
        <Box width="100%">
          {/* Route */}
          <Box
            bgGradient="linear(to-r, blue.900, blue.700)"
            borderRadius="2xl"
            p={5}
            color="white"
            boxShadow="xl"
            mb={5}
          >
            <Text
              fontSize="xs"
              textTransform="uppercase"
              opacity={0.8}
              letterSpacing="1px"
            >
              Trip Route
            </Text>

            <Text
              fontSize="2xl"
              fontWeight="800"
              mt={2}
            >
              {route}
            </Text>

            <Flex align="center" mt={3}>
              <Calendar size={16} />
              <Text ml={2} fontSize="sm">
                {trip.departure_time
                  ? formatDate(trip.departure_time)
                  : "Departure time not specified"}
              </Text>
            </Flex>
          </Box>

          {/* Status */}
          <Flex
            justify="space-between"
            align="center"
            mb={5}
          >
            <Text
              fontSize="md"
              fontWeight="500"
              color="gray.500"
            >
              Trip Status
            </Text>

            <StatusBadge
              label={trip.status || "Unknown"}
              variant={getStatusVariant(trip.status)}
            />
          </Flex>

          <Divider my={5} />

          {/* Trip Information */}
          <Box mb={6}>
            <Text
              fontSize="md"
              fontWeight="500"
              color="gray.500"
              mb={3}
            >
              Trip Information
            </Text>

            <Stack spacing={3}>
              <InfoRow
                label="Origin"
                value={trip.origin || "N/A"}
                icon={MapPin}
              />

              <InfoRow
                label="Destination"
                value={trip.destination || "N/A"}
                icon={Navigation}
              />

              <InfoRow
                label="Intermediate Stops"
                value={
                  trip.intermediate_stops ||
                  "No intermediate stops"
                }
                icon={CircleDot}
              />

              <InfoRow
                label="Vehicle Type"
                value={trip.vehicle_type || "N/A"}
                icon={Truck}
              />

              <InfoRow
                label="Departure"
                value={
                  trip.departure_time
                    ? formatDate(trip.departure_time)
                    : "N/A"
                }
                icon={Clock}
              />
            </Stack>
          </Box>

          <Divider my={5} />

       {/* Capacity */}
<Box mb={6}>
  <Text
    fontSize="md"
    fontWeight="500"
    color="gray.500"
    mb={3}
  >
    Capacity
  </Text>

  <Stack spacing={3}>
    <InfoRow
      label="Packages Booked"
      value={trip.booked_packages || 0}
      icon={Package}
    />

    <InfoRow
      label="Package Capacity"
      value={trip.capacity_packages || 0}
      icon={Package}
    />

    <InfoRow
      label="Available Spaces"
      value={Math.max(
        0,
        (trip.capacity_packages || 0) -
          (trip.booked_packages || 0)
      )}
      icon={Package}
    />
  </Stack>
</Box>

<Divider my={5} />

{/* Booked Packages */}
<Box mb={6}>
  <Text
    fontSize="md"
    fontWeight="500"
    color="gray.500"
    mb={3}
  >
    Booked Packages
  </Text>

  {trip.packages?.length > 0 ? (
    <Stack spacing={2}>
      {trip.packages.map((pkg, index) => (
        <Flex
          key={pkg.package_id || index}
          align="center"
          justify="space-between"
          bg="gray.50"
          borderRadius="md"
          px={4}
          py={3}
        >
          <Flex align="center">
            <Package size={18} color="gray"/>

            <Text
              ml={3}
              fontSize="sm"
              fontWeight="600"
              color="gray.500"
            >
              {pkg.package_number ||
                pkg.package_code ||
                pkg.package_id ||
                "N/A"}
            </Text>
          </Flex>

          <Text
            fontSize="xs"
            color="gray.500"
          >
            Package {index + 1}
          </Text>
        </Flex>
      ))}
    </Stack>
  ) : (
    <Text
      fontSize="sm"
      color="gray.500"
    >
      No packages booked for this trip.
    </Text>
  )}
</Box>
          <Divider my={5} />

          {/* Created */}
          <Box>
            <Text
              fontSize="md"
              fontWeight="500"
              color="gray.500"
              mb={3}
            >
              Trip Record
            </Text>

            <InfoRow
              label="Date Created"
              value={
                trip.created_at
                  ? formatDate(trip.created_at)
                  : "N/A"
              }
              icon={Calendar}
            />
          </Box>
        </Box>
      }
      footerContent={
        <Flex justify="flex-end">
          <GradientButton
            onClick={onClose}
            fontSize="15px"
            width="100px"
            height="40px"
          >
            Close
          </GradientButton>
        </Flex>
      }
    />
  );
};

export default ViewDriverTrip;